// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { cn } from "@/lib/utils"

export interface ChipItem {
  value: string
  label: string
}

interface ChipMultiSelectProps {
  legend: string
  items: ChipItem[]
  /**
   * Contract shape: `explicitListOrNone` — either the literal "none" or a
   * non-empty-checked array once the user has answered. `undefined` means
   * "not answered yet" and renders with nothing selected — including the
   * "None" chip — so an unanswered field never looks like an explicit
   * choice was already made (item 30). `onChange` always reports a real
   * `explicitListOrNone` value once the user acts.
   */
  value: string[] | "none" | undefined
  onChange: (next: string[] | "none") => void
  noneLabel: string
}

/**
 * Multi-select chip group with an explicit, mutually-exclusive "None"
 * chip — mirrors `explicitListOrNone` (packages/contracts/src/schemas/primitives.ts):
 * the value is either the literal "none" or a non-empty array, never both
 * and never neither, so "no answer yet" always fails step validation
 * instead of silently defaulting to one or the other.
 */
export function ChipMultiSelect({
  legend,
  items,
  value,
  onChange,
  noneLabel,
}: ChipMultiSelectProps) {
  const selected = value === "none" || value === undefined ? [] : value
  const isNone = value === "none"

  function toggle(v: string) {
    const next = selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]
    onChange(next.length === 0 ? "none" : next)
  }

  function selectNone() {
    onChange("none")
  }

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="sr-only">{legend}</legend>
      <div className="flex flex-wrap gap-2" role="group" aria-label={legend}>
        {items.map(item => {
          const active = !isNone && selected.includes(item.value)
          return (
            <button
              key={item.value}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(item.value)}
              className={cn(
                "min-h-11 rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                active
                  ? "border-green bg-green/10 text-ink"
                  : "border-line bg-paper text-ink hover:border-green/60"
              )}
            >
              {item.label}
            </button>
          )
        })}
        <button
          type="button"
          aria-pressed={isNone}
          onClick={selectNone}
          className={cn(
            "min-h-11 rounded-full border px-4 py-2 text-sm font-semibold transition-colors",
            isNone
              ? "border-ink bg-ink text-paper"
              : "border-line bg-paper text-ink hover:border-ink/60"
          )}
        >
          {noneLabel}
        </button>
      </div>
    </fieldset>
  )
}
